import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import useCanonicalTag from '../hooks/useCanonicalTag';
import useMetaTags from '../hooks/useMetaTags';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getSeriesDetail } from '../services/api';
import '../styles/fonts.css';
import '../styles/championships.css';
import '../styles/series.css';

function SeriesPage() {
  const { leagueId, seriesId } = useParams();
  const { t } = useTranslation();
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  usePageTitle(series?.name || null);
  useCanonicalTag();
  useMetaTags({
    title: series ? `${series.name} | Celeritas` : 'Celeritas',
    description: series?.name ? `Series standings and championships for ${series.name} on Celeritas` : 'iRacing series on Celeritas',
    url: `https://celeritas.live/leagues/${leagueId}/series/${seriesId}`
  });

  useEffect(() => {
    getSeriesDetail(leagueId, seriesId)
      .then(data => setSeries(data))
      .catch(() => setError(t('series.error')))
      .finally(() => setLoading(false));
  }, [leagueId, seriesId]);

  const allChamps = useMemo(() => series?.championships || [], [series]);

  // Agrupa por ano (desc), dentro de cada ano ordena por quarter desc
  const byYear = useMemo(() => {
    const years = [...new Set(allChamps.map(c => c.season_year || 0))].sort((a, b) => b - a);
    return years.map(year => ({
      year,
      champs: allChamps
        .filter(c => (c.season_year || 0) === year)
        .sort((a, b) => (b.season_quarter || 0) - (a.season_quarter || 0)),
    }));
  }, [allChamps]);

  if (loading) return (
    <div className="ChampionshipsPage">
      <Header />
      <main><p style={{ color: 'white', textAlign: 'center', padding: '40px' }}>{t('series.loading')}</p></main>
      <Footer />
    </div>
  );

  if (error || !series) return (
    <div className="ChampionshipsPage">
      <Header />
      <main><p style={{ color: 'red', textAlign: 'center', padding: '40px' }}>{error || t('series.notFound')}</p></main>
      <Footer />
    </div>
  );

  return (
    <div className="ChampionshipsPage">
      <Header />

      <main>
        <div style={{ maxWidth: '1600px', margin: '20px auto', padding: '0 16px' }}>
          <section className="leagues-archive">

            <div className="box-header" style={{ marginBottom: '20px' }}>
              <nav style={{ fontSize: '12px', color: '#9ba3ab', marginBottom: '6px' }}>
                <Link to="/leagues" style={{ color: '#9ba3ab', textDecoration: 'none' }}>{t('common.leagues')}</Link>
                {' > '}
                <Link to={`/leagues/${series.league_league_id}`} style={{ color: '#9ba3ab', textDecoration: 'none' }}>
                  {series.league_name}
                </Link>
                {' > '}
                <span style={{ color: '#fff' }}>{series.name}</span>
              </nav>
              <h3>{series.name}</h3>
            </div>

            {allChamps.length === 0 ? (
              <p style={{ color: '#9ba3ab', textAlign: 'center', padding: '30px' }}>
                {t('series.noChampionships')}
              </p>
            ) : (
              byYear.map(({ year, champs }) => (
                <div key={year} className="series-year-section">
                  <div className="series-year-label">{year || t('series.noYear')}</div>
                  <div className="series-champs-grid">
                    {champs.map(champ => (
                      <Link
                        key={champ.championship_id}
                        to={`/championships/${champ.championship_id}`}
                        className="series-champ-card"
                      >
                        <div className="series-champ-logo">
                          <img
                            src={champ.league_logo || '/logo-topone.png'}
                            alt=""
                          />
                        </div>
                        <div className="series-champ-name">{champ.name}</div>
                        <div className="series-champ-meta">
                          {champ.season_quarter && (
                            <span className="season-badge">S{champ.season_quarter}</span>
                          )}
                          <span className={champ.active ? 'status-live' : 'status-finished'}>
                            {champ.active ? t('common.active') : t('common.finished')}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))
            )}

          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default SeriesPage;
